/**
 * metricLabels — turn raw backend metric slugs into human-readable labels for
 * the canvas artifacts (ApplicationProfileCard scoring-weight + hard-constraint
 * labels).
 *
 * The frontend has no metric glossary; the ranking/profile tools emit raw column
 * slugs (e.g. `electrical_r_dc_25`). This is a small curated map covering the
 * scoring/constraint metrics used across the 5 branch profiles, with a humanising
 * fallback so any unmapped slug still reads sensibly rather than showing the raw
 * column name.
 *
 * (If the backend later supplies display labels alongside slugs — see Slice 2 —
 * prefer those and treat this as the fallback.)
 */

const METRIC_LABELS = {
  // Energy
  electrical_w_c10_grav: 'Gravimetric Energy',
  electrical_w_c10_vol: 'Volumetric Energy',
  electrical_w_c10: 'Energy (C/10)',
  // Power
  electrical_p_peak_grav: 'Gravimetric Power',
  electrical_p_peak_vol: 'Volumetric Power',
  electrical_p_peak: 'Peak Power',
  // Current
  electrical_i_cont: 'Continuous Current',
  electrical_i_peak: 'Peak Current',
  electrical_i_charge: 'Charge Current',
  // Capacity
  electrical_q_cell_ah: 'Capacity',
  electrical_c_c10: 'Capacity (C/10)',
  // Resistance
  electrical_r_dc_25: 'DC Resistance (25 °C)',
  electrical_r_dc: 'DC Resistance',
  // Voltage
  electrical_v_mean: 'Mean Voltage',
  electrical_v_nom: 'Nominal Voltage',
  // Mechanical
  mechanical_m_cell: 'Cell Mass',
  mechanical_format: 'Format',
  mechanical_volume: 'Cell Volume',
  // Chemistry / lifecycle (proprietary lifecycle metrics may surface in prose)
  chemical_main_chemistry: 'Chemistry',
  cycle_life: 'Cycle Life',
  calendar_life: 'Calendar Life',
}

const DOMAIN_PREFIXES = ['electrical_', 'mechanical_', 'chemical_', 'thermal_', 'general_']

/**
 * Human label for a metric slug. Curated map first, then a humanising fallback
 * (strip the domain prefix, expand grav/vol/temperature suffixes, title-case).
 * @param {string} slug
 * @returns {string}
 */
export function labelForMetric(slug) {
  if (typeof slug !== 'string' || !slug) return String(slug ?? '')
  if (METRIC_LABELS[slug]) return METRIC_LABELS[slug]

  let s = slug
  for (const p of DOMAIN_PREFIXES) {
    if (s.startsWith(p)) {
      s = s.slice(p.length)
      break
    }
  }
  s = s
    .replace(/_grav\b/, ' (grav.)')
    .replace(/_vol\b/, ' (vol.)')
    .replace(/_25\b/, ' (25 °C)')
    .replace(/_/g, ' ')
    .trim()
  return s.replace(/\b\w/g, (c) => c.toUpperCase())
}
